import { useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';

type InputMode = 'text' | 'url';

interface AnalysisResult {
  analysis: string;
  source_type: string;
  source_url: string | null;
  page_title: string | null;
  word_count: number;
  crawl_method: string | null;
  input_tokens: number;
  output_tokens: number;
}

export default function ArticleUpdatePage() {
  const [inputMode, setInputMode] = useState<InputMode>('url');
  const [articleText, setArticleText] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const handleAnalyze = async () => {
    const hasInput = inputMode === 'url' ? articleUrl.trim() : articleText.trim();
    if (!hasInput) {
      setError(inputMode === 'url' ? 'Please enter a URL' : 'Please paste your article text');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const res = await api.post('/articleupdate/analyze', {
        text: inputMode === 'text' ? articleText : '',
        url: inputMode === 'url' ? articleUrl : '',
      });

      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setArticleText('');
    setArticleUrl('');
    setResult(null);
    setError(null);
  };

  const copyToClipboard = () => {
    if (result?.analysis) {
      navigator.clipboard.writeText(result.analysis);
    }
  };

  // Parse the markdown-like result into sections
  const parseResult = (text: string) => {
    const sections: { title: string; content: string }[] = [];
    const lines = text.split('\n');
    let currentTitle = '';
    let currentContent: string[] = [];

    for (const line of lines) {
      const match = line.match(/^\*\*(.+?):\*\*\s*(.*)/);
      if (match) {
        if (currentTitle) {
          sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
        }
        currentTitle = match[1];
        currentContent = match[2] ? [match[2]] : [];
      } else {
        currentContent.push(line);
      }
    }
    if (currentTitle) {
      sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
    }

    return sections;
  };

  const sectionColors: Record<string, { bg: string; border: string; icon: string }> = {
    'The New Angle': { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
    "Why It's Timely": { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600' },
    "Why It's Genuinely New": { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600' },
    "Why It Attracts New Queries": { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600' },
  };

  const sectionIcons: Record<string, JSX.Element> = {
    'The New Angle': (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    "Why It's Timely": (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    "Why It's Genuinely New": (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    "Why It Attracts New Queries": (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Article Update Angle</h1>
          <p className="text-gray-500 mt-1">
            Find a fresh angle to update your existing content and signal a genuine refresh to Google
                <a href="https://drive.google.com/file/d/1TmRqQSlfcZi0BuBCHTSsNfRbyOfXN7SA/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
          {/* Input Mode Toggle */}
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm font-medium text-gray-700">Input:</span>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setInputMode('url')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                  inputMode === 'url' ? 'bg-white shadow text-brand-600' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                URL
              </button>
              <button
                onClick={() => setInputMode('text')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                  inputMode === 'text' ? 'bg-white shadow text-brand-600' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Paste Text
              </button>
            </div>
          </div>

          {/* URL Input */}
          {inputMode === 'url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Article URL</label>
              <input
                type="url"
                value={articleUrl}
                onChange={(e) => setArticleUrl(e.target.value)}
                placeholder="https://example.com/your-article"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">We'll fetch and extract the article content automatically</p>
            </div>
          )}

          {/* Text Input */}
          {inputMode === 'text' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Article Text</label>
              <textarea
                value={articleText}
                onChange={(e) => setArticleText(e.target.value)}
                placeholder="Paste your full article text here..."
                rows={12}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm resize-none"
              />
              <p className="mt-1 text-xs text-gray-400">
                {articleText ? `${articleText.split(/\s+/).filter(Boolean).length.toLocaleString()} words` : 'Paste the full article for best results'}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="px-6 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Analyzing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Find Fresh Angle
                </>
              )}
            </button>
            {(articleText || articleUrl || result) && (
              <button
                onClick={handleClear}
                className="px-4 py-2.5 text-sm text-gray-600 bg-white border rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl border shadow-sm p-12 text-center mb-6">
            <div className="animate-spin w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Analyzing your article...</p>
            <p className="mt-1 text-sm text-gray-400">Finding fresh angles and current trends</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="space-y-4">
            {/* Source Info Bar */}
            <div className="bg-gray-50 rounded-lg border px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                {result.page_title && (
                  <>
                    <span className="font-medium text-gray-800">{result.page_title}</span>
                    <span className="text-gray-300">|</span>
                  </>
                )}
                <span>
                  {result.source_type === 'url' ? (
                    <a href={result.source_url || '#'} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{result.source_url}</a>
                  ) : 'Pasted text'}
                </span>
                <span className="text-gray-300">|</span>
                <span>{result.word_count.toLocaleString()} words analyzed</span>
                {result.crawl_method && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                      {result.crawl_method}
                    </span>
                  </>
                )}
                <span className="text-gray-300">|</span>
                <span className="text-xs text-gray-400">{result.input_tokens + result.output_tokens} tokens</span>
              </div>
              <button
                onClick={copyToClipboard}
                className="px-3 py-1 text-xs bg-white border rounded-md hover:bg-gray-50 text-gray-600 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy
              </button>
            </div>

            {/* Parsed Sections */}
            {parseResult(result.analysis).map((section, i) => {
              const colors = sectionColors[section.title] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600' };
              const icon = sectionIcons[section.title];
              return (
                <div key={i} className={`${colors.bg} border ${colors.border} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-2">
                    {icon && <span className={colors.icon}>{icon}</span>}
                    <h3 className="font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-7 whitespace-pre-line [&]:my-1">{section.content}</p>
                </div>
              );
            })}

            {/* Raw fallback if parsing didn't work well */}
            {parseResult(result.analysis).length === 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-6">
                <div className="prose max-w-none whitespace-pre-line text-gray-700 leading-relaxed">
                  {result.analysis}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
